import type { Step } from 'shepherd.js'
import type { TourDefinition } from '../registry'
import { back, finish, gotIt, next } from '../utils/buttons'
import { attachInteractiveStep } from '../utils/interactive-step'
import { popoverOffset } from '../utils/popover-offset'

/**
 * Walks a user through the share-account dialog the first time they open it.
 * Trigger is `auto` because Radix only mounts DialogContent while open —
 * `useAutoTour('share-dialog-intro')` is rendered inside the dialog so the
 * tour effectively fires "first time the dialog opens, per device".
 *
 * 7-step flow:
 *   1. Centered welcome — orient the user
 *   2. Recipients ("To")
 *   3. Personal message (optional note)
 *   4. Customize content (contacts + sections together)
 *   5. Preview — introduce the live preview region
 *   6. Triage toggle — interactive demo: uncheck the triage box, watch the
 *      preview update on the right
 *   7. Send — points at the "Open Email Client" button, centered popover
 */
export const shareDialogIntroTour: TourDefinition = {
  id: 'share-dialog-intro',
  displayName: 'Share dialog intro',
  featureFlag: 'tour-share-dialog-intro',
  trigger: 'auto',
  // Dev-panel jumps to /accounts; the user opens an account and clicks Share.
  // useAutoTour inside the dialog fires once Radix mounts DialogContent.
  targetPath: '/accounts',
  steps: [
    {
      id: 'welcome',
      title: 'New to sharing? Start the tour!',
      text:
        'This lets you share an account with anyone via email — search teammates by name, paste any external address, customize what details to include, and we\'ll generate a draft you can review before sending.',
      buttons: [gotIt],
    },
    {
      id: 'recipients',
      title: 'Who should see this',
      text: 'Search teammates by name, or paste any email address — internal or external.',
      attachTo: { element: '[data-tour="share-recipients"]', on: 'bottom' },
      floatingUIOptions: popoverOffset(0, 12),
      buttons: [back, next],
    },
    {
      id: 'personal-message',
      title: 'Add a personal note',
      text: 'Optional. Leave a short message at the top of the email so recipients know why you\'re sharing this account. Skip it to let the data speak for itself.',
      attachTo: { element: '[data-tour="share-personal-message"]', on: 'bottom' },
      floatingUIOptions: popoverOffset(0, 12),
      buttons: [back, next],
    },
    {
      id: 'customize-content',
      title: 'Choose what to share',
      text: 'Attach contacts from the account and pick which sections of intelligence to include — triage justification, key strengths, production status, competitors, and more. Sections without data are disabled.',
      attachTo: { element: '[data-tour="share-customize-content"]', on: 'right-start' },
      // Push the popover slightly into the preview area so it sits clear of
      // the customize-content section's right edge.
      floatingUIOptions: popoverOffset(20, 0),
      buttons: [back, next],
    },
    {
      id: 'preview',
      title: 'Preview before you send',
      text: 'Everything you select is reflected in this live preview — recipients, your note, the data sections, even the contacts. What you see here is exactly what your recipients will get.',
      // Anchor: the entire preview column. on: 'left-start' puts the popover
      // top-left, immediately to the left of the email preview box. The
      // -30px x offset gives a clearer gap between popover and preview.
      attachTo: { element: '[data-tour="share-preview"]', on: 'left-start' },
      floatingUIOptions: popoverOffset(-30, 0),
      buttons: [back, next],
    },
    {
      id: 'triage-toggle',
      title: 'Try it out',
      text: 'Uncheck "Triage Justification" on the left and watch it disappear from the preview on the right. Every section works the same way — toggle off, it\'s gone from the email.',
      // Anchor on the checkbox row (the thing the user clicks) and use
      // Shepherd's built-in `extraHighlights` to cut a second hole over the
      // email preview so both regions are spotlit. Shepherd handles the
      // multi-cutout path automatically — no custom SVG manipulation.
      // Popover sits above the row (top-start) so it doesn't cover the
      // preview, with a small upward offset to clear the row.
      attachTo: { element: '[data-tour="share-triage-checkbox"]', on: 'top-start' },
      extraHighlights: ['[data-tour="share-preview"]'],
      floatingUIOptions: popoverOffset(0, -12),
      // Shepherd resolves `extraHighlights` once at step-show and does NOT
      // reposition the second cutout on scroll/resize. Disabling scrollTo
      // here keeps the dialog stationary so both cutouts stay aligned for
      // the duration of the step.
      scrollTo: false,
      buttons: [back, next],
    },
    {
      id: 'send',
      title: 'Open it in your email client',
      text:
        'When you\'re ready to send the email, click "Open Email Client" — we\'ll open your default mail app with everything pre-filled. You review and hit send. That\'s it.',
      // Omitting `on` keeps the popover centered on the screen while
      // Shepherd still cuts the spotlight around the Open Email Client
      // button. Documented Shepherd behaviour: "If you omit the on portion
      // of attachTo, the element will still be highlighted, but the tooltip
      // will appear in the middle of the screen, without an arrow."
      attachTo: { element: '[data-tour="share-send-btn"]' },
      // Bridge: add a placeholder recipient so the Open Email Client button
      // looks enabled during this step. The dialog listens for these events
      // and adds/removes scout@supplyco.ai from `recipients`. Teardown fires
      // on back, finish, and cancel — see attachInteractiveStep.
      when: {
        show(this: Step) {
          attachInteractiveStep(this, {
            setup: () =>
              globalThis.dispatchEvent(new CustomEvent('tour-share-add-test-recipient')),
            teardown: () =>
              globalThis.dispatchEvent(new CustomEvent('tour-share-remove-test-recipient')),
          })
        },
      },
      buttons: [back, finish],
    },
  ],
}
